import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import {
  type Agent,
  ExpertReviewAgent,
  CalendarAgent,
  ReaderReactionsAgent,
  GmailAgent,
  type ReplaceAgent,
} from '../utils/cards'
import { Step } from '../utils/steps'

import { useStep } from './use-step'

const getAnimationBaseTimings = (): {
  addMail: number
  addCalendar: number
  removeMail: number
  removeCalendar: number
  intervalDelay: number
} => {
  // the agent icon animation time
  const ANIMATION_DELAY = 300
  // a little delay (2s) before adding mail
  const ADD_MAIL = 2000
  // after adding mail animated, pause ~0.5s then add calendar
  const ADD_CALENDAR = ADD_MAIL + ANIMATION_DELAY + 500
  // after adding calendar animated, pause ~2s and remove mail
  const REMOVE_MAIL = ADD_CALENDAR + ANIMATION_DELAY + 2000
  // wait for remove mail animated, pause 0.5s and remove calendar
  const REMOVE_CALENDAR = REMOVE_MAIL + ANIMATION_DELAY + 500
  // wait for remove calendar animated then start again
  const INTERVAL_DELAY = REMOVE_CALENDAR + ANIMATION_DELAY

  return {
    addMail: ADD_MAIL,
    addCalendar: ADD_CALENDAR,
    removeMail: REMOVE_MAIL,
    removeCalendar: REMOVE_CALENDAR,
    intervalDelay: INTERVAL_DELAY,
  }
}

const createAnimationSequence = (timings: ReturnType<typeof getAnimationBaseTimings>) => {
  return [
    { delay: timings.addMail, replaceAgents: [{ index: 4, agent: GmailAgent }] },
    { delay: timings.addCalendar, replaceAgents: [{ index: 5, agent: CalendarAgent }] },
    { delay: timings.removeMail, replaceAgents: [{ index: 4, agent: ExpertReviewAgent }] },
    { delay: timings.removeCalendar, replaceAgents: [{ index: 5, agent: ReaderReactionsAgent }] },
  ]
}

function getAdjustedTiming(
  offset: number,
  baseTimings: ReturnType<typeof getAnimationBaseTimings>,
) {
  const timing = { ...baseTimings }
  for (const key in timing) {
    if (key === 'intervalDelay') {
      continue
    }

    const keyName = key as keyof typeof baseTimings
    timing[keyName] =
      (baseTimings[keyName] + baseTimings.intervalDelay - offset) % baseTimings.intervalDelay
  }
  return timing
}

const getAdjustedTimings = (
  currentAgentName: { index4: string; index5: string },
  baseTimings: ReturnType<typeof getAnimationBaseTimings>,
) => {
  const { index4, index5 } = currentAgentName

  // If only mail is already added, start from add calendar
  if (index4 === GmailAgent.name && index5 === ReaderReactionsAgent.name) {
    return getAdjustedTiming(baseTimings.addMail, baseTimings)
  }

  // If both are already added, start from remove mail
  if (index4 === GmailAgent.name && index5 === CalendarAgent.name) {
    return getAdjustedTiming(baseTimings.addCalendar, baseTimings)
  }

  // If only calendar is added, start from remove calendar
  if (index4 === ExpertReviewAgent.name && index5 === CalendarAgent.name) {
    return getAdjustedTiming(baseTimings.removeMail, baseTimings)
  }

  return baseTimings
}

const scheduleAnimation = (
  setReplaceAgents: (replaceAgents: ReplaceAgent[]) => void,
  timings: ReturnType<typeof getAnimationBaseTimings>,
) => {
  const sequence = createAnimationSequence(timings)
  return sequence.map(({ delay, replaceAgents }) =>
    setTimeout(
      () =>
        setReplaceAgents(
          replaceAgents.map(({ index, agent }) => ({
            index,
            agent: { ...agent, hideName: true },
          })),
        ),
      delay,
    ),
  )
}

export const useAddAgents = (setAgents: Dispatch<SetStateAction<Agent[]>>, agents: Agent[]) => {
  const { step } = useStep()
  const [replaceAgents, setReplaceAgents] = useState<ReplaceAgent[]>([])

  useEffect(() => {
    if (step !== Step.Plus || agents.length < 6) {
      // if user scroll too quickly, it is possible we are in the plus step but only have one agent.
      // so we need to check if the agents length

      setReplaceAgents(agents => {
        // reset hideName when user scroll out of the plus step
        if (agents.some(({ agent }) => agent.hideName)) {
          return agents.map(({ index, agent }) => ({ index, agent: { ...agent, hideName: false } }))
        }
        return agents
      })
      return
    }

    const timeoutIds: NodeJS.Timeout[] = []
    const baseTimings = getAnimationBaseTimings()
    const currentAgentName = {
      index4: agents[4].name,
      index5: agents[5].name,
    }
    const adjustedTimings = getAdjustedTimings(currentAgentName, baseTimings)

    const clearTimeouts = () => {
      for (const timeoutId of timeoutIds) {
        clearTimeout(timeoutId)
      }
      timeoutIds.length = 0
    }

    // Schedule the animation sequence for the first time
    const newTimeoutIds = scheduleAnimation(setReplaceAgents, adjustedTimings)
    timeoutIds.push(...newTimeoutIds)

    // Set up the repeating interval
    const interval = setInterval(() => {
      clearTimeouts()
      const newTimeoutIds = scheduleAnimation(setReplaceAgents, baseTimings)
      timeoutIds.push(...newTimeoutIds)
    }, baseTimings.intervalDelay)

    return () => {
      clearInterval(interval)
      clearTimeouts()
    }
  }, [step, agents, setReplaceAgents])

  useEffect(() => {
    if (agents.length < 6 || step !== Step.Plus) {
      setAgents(agents => {
        if (agents.some(agent => agent.hideName)) {
          return agents.map(agent => ({ ...agent, hideName: false }))
        }
        return agents
      })
      return
    }

    const needUpdate = replaceAgents.some(({ index, agent }) => agents[index].name !== agent.name)
    if (!needUpdate) {
      return
    }

    const newAgents = [...agents]

    for (const { index, agent } of replaceAgents) {
      newAgents[index] = agent
    }

    setAgents(newAgents)
  }, [replaceAgents, agents, setAgents, step])
}
